<template>
  <div :class="$attrs.class">
    <label v-if="label" class="form-label" :for="id">{{ label }}:</label>
    <div class="relative">
      <div class="flex">
        <!-- Country Trigger -->
        <button 
          type="button" 
          class="flex items-center justify-between bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg px-3 py-2 hover:bg-gray-100 focus:outline-none focus:border-orange-500 transition-colors bg-white"
          style="min-width: 80px;"
          @click="toggleDropdown"
        >
          <span class="text-xl leading-none">{{ selectedCountry.flag }}</span>
          <i class="fas fa-chevron-down text-xs text-gray-500 ml-2" />
        </button>

        <!-- Phone Number Input -->
        <input 
          :id="id" 
          ref="input" 
          v-bind="{ ...$attrs, class: null }" 
          v-model="formattedInput" 
          class="form-input rounded-l-none flex-1" 
          :class="{ error: error }"
          type="tel"
          :placeholder="placeholder" 
          @input="handleInput"
        />
      </div>

      <!-- Custom Dropdown Menu -->
      <div v-if="isOpen" class="absolute z-50 top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden animate-fade-in-down">
        <!-- Search Box -->
        <div class="p-2 border-b border-gray-100 bg-gray-50">
          <div class="relative">
            <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input 
              ref="searchInput"
              v-model="searchQuery" 
              type="text" 
              class="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500" 
              placeholder="Buscar país..." 
            />
          </div>
        </div>
          
        <!-- Country List -->
        <ul class="max-h-60 overflow-y-auto">
          <li 
            v-for="country in filteredCountries" 
            :key="country.code" 
            class="px-4 py-2 hover:bg-orange-50 cursor-pointer flex items-center gap-3 transition-colors"
            :class="{'bg-orange-50': country.dial_code === countryCode}"
            @click="selectCountry(country)"
          >
            <span class="text-xl">{{ country.flag }}</span>
            <div class="flex flex-col">
              <span class="text-sm font-medium text-gray-700">{{ country.name }}</span>
              <span class="text-xs text-gray-500">{{ country.dial_code }}</span>
            </div>
          </li>
          <li v-if="filteredCountries.length === 0" class="px-4 py-3 text-center text-sm text-gray-500">
            No se encontraron resultados
          </li>
        </ul>
      </div>
    </div>
    <div v-if="error" class="form-error">{{ error }}</div>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid'

export default {
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      default() {
        return `phone-input-${uuid()}`
      },
    },
    error: String,
    label: String,
    modelValue: String,
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      countryCode: '+58',
      phoneNumber: '',
      countries: [
        { name: 'Venezuela', dial_code: '+58', flag: '🇻🇪' },
        { name: 'Colombia', dial_code: '+57', flag: '🇨🇴' },
        { name: 'Estados Unidos', dial_code: '+1', flag: '🇺🇸' },
        { name: 'España', dial_code: '+34', flag: '🇪🇸' },
        { name: 'Argentina', dial_code: '+54', flag: '🇦🇷' },
        { name: 'Chile', dial_code: '+56', flag: '🇨🇱' },
        { name: 'Perú', dial_code: '+51', flag: '🇵🇪' },
        { name: 'México', dial_code: '+52', flag: '🇲🇽' },
        { name: 'Panamá', dial_code: '+507', flag: '🇵🇦' },
        { name: 'Dominicana', dial_code: '+1-809', flag: '🇩🇴' },
      ],
    }
  },
  computed: {
    selectedCountry() {
      return this.countries.find(c => c.dial_code === this.countryCode) || this.countries[0]
    },
    filteredCountries() {
      const query = this.searchQuery.toLowerCase()
      return this.countries.filter(c => 
        c.name.toLowerCase().includes(query) || 
              c.dial_code.includes(query),
      )
    },
    placeholder() {
      return `${this.countryCode} 412 123 4567`
    },
    formattedInput: {
      get() {
        return this.phoneNumber
      },
      set(val) {
        // Allow user to clear input freely
        this.phoneNumber = val
      },
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        if (!val) return
        // Attempt to extract country code and number
        // Strategy: Check if starts with any known dial_code
        // Sort dial_codes by length desc to match +1-809 before +1
        const sortedCountries = [...this.countries].sort((a,b) => b.dial_code.length - a.dial_code.length)
            
        const match = sortedCountries.find(c => val.startsWith(c.dial_code))
        if (match) {
          this.countryCode = match.dial_code
          this.phoneNumber = val.substring(match.dial_code.length).trim()
        } else {
          // Fallback if no code matches or plain number
          // If it's just a number without +, assume existing countryCode
        }
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.closeDropdown)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeDropdown)
  },
  methods: {
    toggleDropdown(e) {
      e.stopPropagation()
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.$nextTick(() => {
          this.$refs.searchInput.focus()
        })
      }
    },
    closeDropdown(e) {
      if (this.$el && !this.$el.contains(e.target)) {
        this.isOpen = false
      }
    },
    selectCountry(country) {
      this.countryCode = country.dial_code
      this.isOpen = false
      this.searchQuery = ''
      this.updateValue()
      this.$refs.input.focus()
    },
    handleInput(e) {
      // Simple formatting could go here
      this.phoneNumber = e.target.value.replace(/[^\d\s]/g, '')
      this.updateValue()
    },
    updateValue() {
      // Emit full formatted number
      const val = `${this.countryCode} ${this.phoneNumber}`
      this.$emit('update:modelValue', val)
    },
    focus() {
      this.$refs.input.focus()
    },
    select() {
      this.$refs.input.select()
    },
  },
}
</script>

<style scoped>
.animate-fade-in-down {
    animation: fadeInDown 0.2s ease-out;
}
@keyframes fadeInDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
}
</style>
